//#region IMPORT
import Commons from '../../../Services/Common/Common';
import Global from '../../../Services/Common/Global';
import { showSnackbar } from '../../../Reducer/SnackbarReducer';
import dayjs from 'dayjs';
import { apiRequest } from '../../../Services/Common/ApiRequest';
//#endregion

/**
 * 안전보건교육 - 교육이수현황 (SAT_BASIC_03) 전용 로직
 * other 케이스에서 사용
 */

// 안전보건교육 - 교육이수현황 조회 함수
export const SearchLogic1 = async (apiUrl, procedureParams, dispatch, setLoading) => {
  setLoading(true);

  try {
    const result = await apiRequest(
      'post',
      apiUrl,
      procedureParams
    );

    let data = result.data;
    if (data.length === 0) {
      dispatch(
        showSnackbar({
          message: '데이터가 없습니다.',
          type: 'info',
        })
      );
      return [];
    } else if (data[0]?.type === 'Error') {
      dispatch(showSnackbar({ message: data[0].message, type: 'error' }));
      return [];
    } else {
      // 각 SHE_ID에 대해 교육대상자 이름 조회
      const dataWithTargetNames = await Promise.all(
        data.map(async (raw) => {
          if (!raw.sheId || !raw.sheTargetCount || raw.sheTargetCount === 0)
            return raw;

          try {
            const result = await apiRequest(
              'post',
              Global.urls.DocsManagement.getSatBasic05R2,
              {
                SheId: Commons.EncryptAES(raw.sheId),
              }
            );
            const targetData = result.data;

            if (
              Array.isArray(targetData) &&
              targetData.length > 0 &&
              targetData[0]?.type !== 'Error'
            ) {
              const targetNames = targetData
                .map((t) => t.name || t.userNm || t.id || '')
                .filter(Boolean)
                .sort((a, b) => a.localeCompare(b, 'ko'));

              if (targetNames.length > 0) {
                const targetText =
                  targetNames.length === 1
                    ? targetNames[0]
                    : `${targetNames[0]} 외 ${targetNames.length - 1}명`;

                return {
                  ...raw,
                  eduTarget: targetText,
                  eduTargetIds: targetData
                    .map((t) => t.id || t.userid || '')
                    .filter(Boolean),
                };
              }
            }
          } catch (error) {
            console.error(`교육대상자 조회 실패 (sheId: ${raw.sheId}):`, error);
          }

          return raw;
        })
      );

      return Commons.InsertNumber(dataWithTargetNames);
    }
  } catch (e) {
    console.error(e);
    dispatch(showSnackbar({ message: e.message, type: 'error' }));
    return [];
  } finally {
    setLoading(false);
  }
};

// 코드 데이터 조회 함수
const SearchCode = async (dispatch, setLoading) => {
  setLoading(true);
  try {
    const result = await apiRequest(
      'post',
      Global.urls.Utility.getCodeInfo,
    );

    const data = result.data;
    if (!Array.isArray(data) || data.length === 0) {
      dispatch(showSnackbar({ message: '코드가 없습니다.', type: 'info' }));
      return null;
    }

    if (data[0]?.type === 'Error') {
      dispatch(showSnackbar({ message: data[0].message, type: 'error' }));
      return null;
    }

    // SHE_GUBUN 그룹만 필터링
    const sheGubunCodes = data
      .filter((item) => item.groupCd === 'SHE_GUBUN')
      .map((item) => ({
        code: item.code,
        name: item.codeNm,
      }));

    return { sheGubun: sheGubunCodes };
  } catch (e) {
    dispatch(showSnackbar({ message: e.message, type: 'error' }));
    return null;
  } finally {
    setLoading(false);
  }
};

// 안전보건교육 - 교육이수현황 인쇄 함수 (관리자 SAT_BASIC_05F PrintLogic1과 동일 로직)
export const PrintLogic1 = async (
  documentData,
  dispatch,
  setLoading,
  rowData1,
  searchRef
) => {
  setLoading(true);
  try {
    const list = Array.isArray(rowData1) ? rowData1 : rowData1 ? [rowData1] : [];
    if (list.length === 0) {
      dispatch(
        showSnackbar({
          message: '인쇄할 데이터가 없습니다.',
          type: 'warning',
        })
      );
      return null;
    }

    const codes = await SearchCode(dispatch, setLoading);
    if (!codes) {
      dispatch(
        showSnackbar({
          message: '코드 데이터를 가져올 수 없습니다.',
          type: 'error',
        })
      );
      return null;
    }

    const searchYear = searchRef?.current?.currentYear
      ? dayjs(searchRef.current.currentYear, 'YYYY').format('YYYY')
      : searchRef?.current?.year
        ? dayjs(searchRef.current.year, 'YYYY').format('YYYY')
        : dayjs().format('YYYY');

    const headerData = { YEAR: searchYear };

    const codeList = codes?.SHE_GUBUN || codes?.sheGubun || [];

    const tableData = await Promise.all(
      list
        .filter((row) => !row.deleteYn)
        .map(async (row, index) => {
          const sheGubunCode = row.sheGubun || row.SHE_GUBUN || '';
          const foundCode = codeList.find(
            (item) => (item.code || item.CODE) === sheGubunCode
          );
          const sheGubunName = foundCode
            ? (foundCode.name || foundCode.CODE_NM)
            : sheGubunCode;

          let eduTargetText = '';
          if (row.eduTarget || row.EDU_TARGET) {
            eduTargetText = row.eduTarget || row.EDU_TARGET;
          } else {
            const sheId = row.sheId ?? row.SHE_ID;
            const sheTargetCount =
              row.sheTargetCount ?? row.SHE_TARGET_COUNT ?? 0;
            if (sheId && sheTargetCount > 0) {
              try {
                const res = await apiRequest(
                  'post',
                  Global.urls.DocsManagement.getSatBasic05R2,
                  { SheId: Commons.EncryptAES(String(sheId)) || '' }
                );
                const targetData = res?.data;

                if (
                  Array.isArray(targetData) &&
                  targetData.length > 0 &&
                  targetData[0]?.type !== 'Error'
                ) {
                  const targetNames = targetData
                    .map(
                      (t) =>
                        t.name || t.NAME || t.userNm || t.USER_NM || t.id || ''
                    )
                    .filter(Boolean)
                    .sort((a, b) => a.localeCompare(b, 'ko'));
                  if (targetNames.length > 0) {
                    eduTargetText =
                      targetNames.length === 1
                        ? targetNames[0]
                        : `${targetNames[0]} 외 ${targetNames.length - 1}명`;
                  } else {
                    eduTargetText = `${sheTargetCount}명`;
                  }
                } else {
                  eduTargetText = `${sheTargetCount}명`;
                }
              } catch (error) {
                console.error(
                  `교육대상자 조회 실패 (sheId: ${sheId}):`,
                  error
                );
                eduTargetText = `${sheTargetCount}명`;
              }
            } else if (sheTargetCount > 0) {
              eduTargetText = `${sheTargetCount}명`;
            }
          }

          const sheYn = row.sheYn || row.SHE_YN || '';
          let sheYnText = '';
          if (sheYn === 'ON' || sheYn === 'on') {
            sheYnText = '실시';
          } else if (sheYn === 'OFF' || sheYn === 'off') {
            sheYnText = '미실시';
          } else {
            sheYnText = sheYn;
          }

          return {
            NO: index + 1,
            SHE_GUBUN: sheGubunName,
            SHE_NM: row.sheNm || row.SHE_NM || '',
            EDU_TARGET: eduTargetText,
            SHE_TIME: row.sheTime ?? row.SHE_TIME ?? 0,
            SHE_YN: sheYnText,
          };
        })
    );

    const printData = {
      FileName: 'TRA_BASIC_05',
      HeaderData: JSON.stringify(headerData),
      TableData: JSON.stringify(tableData),
    };

    const response = await Commons.Axios(
      'report',
      Global.base.useUrl + Global.urls.Reports.reports,
      printData
    );

    if (response.data instanceof Blob) {
      return response.data;
    }
    if (response.data) {
      return new Blob([response.data], { type: 'application/pdf' });
    }
    console.error('PrintLogic1: 응답 데이터가 없습니다.');
    return null;
  } catch (e) {
    console.error(e);
    dispatch(showSnackbar({ message: e.message, type: 'error' }));
    return null;
  } finally {
    setLoading(false);
  }
};

